/**
 * Renders a choice or knowledge-check scenario in a two-column reading layout: the SITUATION (context
 * + the issue) on the left, the OPTIONS on the right. The options are STAGED — the player reads the
 * situation first and clicks "Proceed" to reveal their choices (so everything doesn't land at once).
 * Committing happens via an inline button on the selected option (no scrolling to a footer). Options
 * are shuffled per run (lib/shuffle.ts) and labelled by display position. Any pop-up interrupt for
 * this scenario is rendered by the parent.
 */
import { useEffect, useRef, useState } from "react";
import type { ChoiceScenario, KnowledgeCheckScenario } from "@patc/content-schema";
import { OptionTile, type OptionMode } from "./OptionTile";
import { ScenarioArt } from "./ScenarioArt";
import { PHASE_LABEL } from "../lib/ui";
import { seededOrder } from "../lib/shuffle";

type Decidable = ChoiceScenario | KnowledgeCheckScenario;

export function ScenarioView({
  scenario,
  seed,
  revealing,
  revealOthers,
  selectedChoiceId,
  onSelect,
  onCommit,
  onContinue,
  isLast,
}: {
  scenario: Decidable;
  /** Per-run seed driving the deterministic option order. */
  seed: number;
  revealing: boolean;
  /** When true (untimed practice), reveal every option's full feedback; timed reveals only tiers. */
  revealOthers: boolean;
  selectedChoiceId: string | null;
  onSelect: (id: string) => void;
  onCommit: () => void;
  onContinue: () => void;
  isLast: boolean;
}) {
  const isChoice = scenario.kind === "choice";
  const orderedChoices = seededOrder(scenario.choices, seed, scenario.id);

  // Staged reveal: show the situation first; options appear on "Proceed". Re-gate for each new scenario,
  // but if a selection is already in play (e.g. resumed mid-decision), show the options straight away so
  // the player isn't sent back behind the gate. Once committed (revealing) the options are always shown.
  const [optionsShown, setOptionsShown] = useState(() => selectedChoiceId != null);
  useEffect(() => {
    setOptionsShown(selectedChoiceId != null);
  }, [scenario.id, selectedChoiceId]);
  const showOptions = optionsShown || revealing;

  // Keep keyboard focus sane: move focus into the options region on the Proceed→options AND Commit→reveal
  // transitions (the control that had focus unmounts). Skip the first mount/resume so we don't steal focus
  // or fight the scroll-to-top.
  const optionsRef = useRef<HTMLDivElement>(null);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    // preventScroll: moving focus must NOT scroll the region under the sticky HUD (which would hide the
    // Continue button at its top); the content is already in view at this point.
    if (showOptions) optionsRef.current?.focus({ preventScroll: true });
  }, [optionsShown, revealing]);

  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <p className="t-overline mb-3 text-brand">
        {PHASE_LABEL[scenario.phase]}
        {isChoice ? "" : " · Knowledge check"}
      </p>
      {scenario.title && (
        <h1 className="text-balance font-display text-fluid-2xl font-semibold leading-tight text-ink">
          {scenario.title}
        </h1>
      )}

      <div className="mt-5 grid gap-8 lg:grid-cols-2">
        {/* LEFT — the situation */}
        <div className="reading-measure flex flex-col gap-4">
          <p className="text-pretty text-fluid-base leading-relaxed text-ink-700">{scenario.description}</p>
          {isChoice && (
            <div className="rounded-sm border-l-2 border-brand bg-panel px-4 py-3">
              <p className="t-overline text-ink-600">The issue</p>
              <p className="mt-1 text-pretty text-fluid-lg leading-relaxed text-ink">
                {(scenario as ChoiceScenario).issue}
              </p>
            </div>
          )}
        </div>

        {/* RIGHT — the options (staged behind Proceed) */}
        <div className="flex flex-col">
          {!showOptions ? (
            <>
              <div className="flex flex-col items-start gap-4 rounded-sm border border-dashed border-ink-300 p-6">
                <p className="text-sm leading-relaxed text-ink-600">
                  Read the situation on the left. When you're ready, reveal your options.
                </p>
                <button
                  type="button"
                  onClick={() => setOptionsShown(true)}
                  className="inline-flex items-center gap-2 rounded-sm bg-brand px-5 py-2.5 font-medium text-white transition-colors hover:bg-brand-hover"
                >
                  Proceed to the options <span aria-hidden="true">→</span>
                </button>
              </div>
              {/* Decorative scenario motif, centred in the right half directly under the Proceed box. */}
              <ScenarioArt scenarioId={scenario.id} />
            </>
          ) : (
            <div ref={optionsRef} tabIndex={-1} className="outline-none">
              {/* After committing, the Continue control sits at the top of the answers column, LEFT-aligned
                  with the legend and the options, so it's reachable without scrolling past every reaction. */}
              {revealing && (
                <div className="mb-5">
                  <button
                    type="button"
                    onClick={onContinue}
                    className="inline-flex items-center gap-2 rounded-sm bg-ink px-5 py-2.5 font-medium text-white transition-colors hover:bg-ink-800"
                  >
                    {isLast ? "See your outcome" : "Continue"}
                    <span aria-hidden="true">→</span>
                  </button>
                </div>
              )}
              <fieldset className="flex flex-col gap-3" disabled={revealing}>
                <legend className="t-overline mb-1 text-ink-500">
                  {revealing
                    ? "Your decision"
                    : isChoice
                      ? "Choose your course of action"
                      : "Select the correct answer"}
                </legend>
                {revealing &&
                  (revealOthers ? (
                    <p className="-mt-1 mb-1 text-xs text-ink-600">
                      Practice mode — every option's full outcome is shown below.
                    </p>
                  ) : (
                    <p className="-mt-1 mb-1 text-xs text-ink-600">
                      Every option's quality is shown; full reactions appear in untimed practice.
                    </p>
                  ))}
                {orderedChoices.map((c, idx) => {
                  const mode: OptionMode = !revealing
                    ? "select"
                    : c.id === selectedChoiceId
                      ? "chosen"
                      : revealOthers
                        ? "revealed"
                        : "revealed-tier";
                  return (
                    <OptionTile
                      key={c.id}
                      choice={c}
                      mode={mode}
                      displayLabel={String.fromCharCode(65 + idx)}
                      selected={selectedChoiceId === c.id}
                      onSelect={() => onSelect(c.id)}
                      onCommit={onCommit}
                    />
                  );
                })}
              </fieldset>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
